import Vue from 'vue'
import browserUtil from './browser-utils'

/**
 * Estado global de instalação do PWA.
 *
 * O evento `beforeinstallprompt` é disparado UMA ÚNICA VEZ por carregamento de
 * página, normalmente logo depois que o service worker fica pronto. Como esta é
 * uma SPA, capturá-lo dentro de um componente (Login) faz o evento se perder ao
 * navegar para outro componente (Install) — que registra o listener tarde
 * demais e nunca recebe nada. Era exatamente isso que deixava o botão
 * "Instalar App" inerte no Android.
 *
 * A captura acontece em `public/index.html` (inline, antes do bundle carregar)
 * e o evento fica em `window.pwaInstallPrompt`. Este módulo assume a posse
 * dele e mantém um estado reativo que qualquer componente pode observar.
 */
const state = Vue.observable({
  prompt: null,
  installed: browserUtil.isInStandaloneMode(),
})

function adotarPrompt(event) {
  state.prompt = event
}

// Evento capturado pelo snippet inline antes do Vue subir.
if (typeof window !== 'undefined') {
  if (window.pwaInstallPrompt) {
    adotarPrompt(window.pwaInstallPrompt)
  }

  // A partir daqui o listener é nosso; o snippet inline se desliga sozinho.
  window.addEventListener('beforeinstallprompt', event => {
    event.preventDefault()
    adotarPrompt(event)
  })

  window.addEventListener('appinstalled', () => {
    state.prompt = null
    state.installed = true
    window.pwaInstallPrompt = null
  })
}

export default {
  /** O navegador já ofereceu o prompt nativo e podemos dispará-lo. */
  get disponivel() {
    return !!state.prompt && !state.installed
  },

  /** App já está rodando instalado (standalone). */
  get instalado() {
    return state.installed
  },

  /**
   * Só faz sentido oferecer instalação onde ela é possível: iOS depende do
   * Safari nativo (instrução manual) e o resto depende do prompt nativo.
   * Navegador embutido de WhatsApp/Instagram/Facebook não instala nada.
   */
  get oferecivel() {
    if (state.installed || browserUtil.isInAppBrowser()) return false
    if (browserUtil.isIosMobile()) return browserUtil.isIosSafari()
    return !!state.prompt
  },

  /**
   * Dispara o prompt nativo. Resolve com `true` se o usuário aceitou.
   * Um prompt só pode ser consumido uma vez — depois disso o evento é
   * descartado e o navegador emitirá um novo em um próximo carregamento.
   */
  async instalar() {
    if (!state.prompt) return false

    const event = state.prompt
    event.prompt()

    try {
      const { outcome } = await event.userChoice
      if (outcome === 'accepted') {
        state.installed = true
        return true
      }
      return false
    } finally {
      state.prompt = null
      window.pwaInstallPrompt = null
    }
  },
}
